/**
 * The single source for what the currency selector says about the rate it applied.
 *
 * Until LEN-2089 this text was three literals inlined in three page templates, and they
 * had already drifted: Overview said "at month-end" while Facility Insights and
 * Concentrations said "as of the cut-off date", describing the same behaviour two ways.
 * All three named only Banxico, so COP - servable since LEN-1962 - was converted through
 * a source the tooltip never mentioned, at a rate no authority certifies, without saying
 * so. Scenario 1 of LEN-1809 asks for one text across five sections precisely so this
 * cannot happen; it had already happened across three.
 *
 * Written as a function rather than a constant on purpose. LEN-2090 has to fold in the
 * applied factor, its date and its staleness, and those vary per chart - a `const` would
 * have to be thrown away to get there, along with every call site reading it.
 *
 * LEN-2090 added the parameter, from the payload rather than from the ticket: the shape
 * is `FxDisclosureWire`, modelled on what `facilities-api` actually sends.
 */
import { DateTime } from 'luxon';

import { FxDisclosureWire } from '@models/API/fx-disclosure.model';

/**
 * LEN-1809 wrote this sentence with "the closing exchange rate" and it was first kept
 * verbatim; review of MR !1203 (B3) surfaced what the wording hid: since LEN-1965 flows
 * convert at the average rate of their period, and Insights puts both kinds of chart on
 * one screen, so for half of them the ticket's sentence named the wrong basis. The
 * sentence now names only the sources, and `WHICH_RATE_APPLIES` states the rule -
 * scenario 3 asks that the applied basis be clear, and a sentence that is wrong for
 * half the charts cannot be that.
 */
const HOW_THE_RATE_IS_CHOSEN =
  'Amounts are converted using the exchange rates published by the central bank ' +
  'of each currency — FIX (Banco de México) for MXN and USD, TRM (Banco de la ' +
  'República) for COP.';

/**
 * The rule, because one page mixes both kinds: balances (Assets, Aging, Borrowing
 * Base) convert at the closing rate, flows (Collection, Core Metrics) at the average
 * of their period, per IAS 21. A page-level tooltip cannot name a single basis
 * without being wrong for half the screen; it can state the rule that decides.
 */
const WHICH_RATE_APPLIES =
  'Balances are converted at the closing rate; flows (collections, payments) at ' +
  'the average rate of their period (IAS 21).';

/**
 * The part a risk analyst needs and the previous text never said. There is no official
 * MXN/COP rate: it is derived by crossing the two published ones through USD, so it is a
 * reasonable number to look at and not one to settle a contract on.
 */
const CROSSED_PAIR_IS_INDICATIVE =
  'MXN/COP has no official direct rate: it is crossed via USD and is indicative, not ' +
  'binding.';

/**
 * Enough decimals to reproduce the figure, which is the whole point of showing it.
 *
 * Not `toFixed(2)`. One peso is roughly 181 COP, so two decimals on that factor already
 * move a billion-peso balance by thousands; and in the other direction the factor is
 * ~0.0055, where two decimals leave nothing at all. Six is what the backend was found to
 * need when it stopped rounding on the way into storage, for the same reason.
 */
const FACTOR_DECIMALS = 6;

/**
 * `2026-07-31` -> `31 Jul 2026`. Left as the raw string if it cannot be parsed.
 *
 * Luxon rather than `new Date` + a hardcoded locale: the same diff already formats the
 * cut-off with `DateTime.fromISO(...).toFormat(...)` (general-overview insights), and
 * two formatters for the same kind of date is how the tooltip and the export drift
 * apart (MR !1203 S6).
 */
function readableDate(isoDate: string): string {
  const parsed = DateTime.fromISO(isoDate);

  return parsed.isValid ? parsed.toFormat('d MMM yyyy') : isoDate;
}

/**
 * The applied rate, spelled out so the reader can multiply and land on the figure.
 *
 * Stated as `1 MXN = 180.763815 COP` rather than as a bare factor because a bare number
 * next to two currency codes does not say which way it goes, and the pair that matters
 * here is derived - getting the direction backwards turns a 181x into a 0.0055x.
 *
 * The basis is part of the label ("Applied closing rate") - criterion 5 of LEN-2090:
 * with both kinds of chart on one screen, a factor that does not say which kind it
 * converted answers the wrong half of the question.
 */
function appliedFactor(fx: FxDisclosureWire): string {
  return `Applied ${fx.basis} rate: 1 ${fx.from} = ${fx.rate.toFixed(FACTOR_DECIMALS)} ${fx.to}`;
}

/**
 * The date the rate actually comes from, and how far it is from what was asked for.
 *
 * `as_of` is the applied date, not the requested one - asking for a Sunday or a
 * Colombian holiday resolves to the previous publication, and saying otherwise would
 * claim an authority published on a day it did not.
 */
function appliedDate(fx: FxDisclosureWire): string {
  if (!fx.as_of) {
    return '';
  }

  const base = ` on ${readableDate(fx.as_of)}`;

  // Only when it is genuinely behind. The backend reports `null` for a rate that is not
  // older, and reported `-1` until LEN-1807's QA round - a negative staleness meant
  // nothing to a reader and should never reach one again.
  if (fx.stale_days && fx.stale_days > 0) {
    const days = fx.stale_days === 1 ? 'day' : 'days';

    return `${base}, which is ${fx.stale_days} ${days} older than the date requested`;
  }

  return base;
}

/**
 * The disclosure shown on the currency selector, identical in every section.
 *
 * @param applied - the rate a chart on this page was converted with. Without it the
 *   text still names the sources and the nature of the crossed pair, which is what
 *   LEN-2089 delivered; with it the reader can reproduce the figure, which is what
 *   LEN-1809 set out to make possible.
 */
export function fxDisclosure(applied?: FxDisclosureWire): string {
  const parts = [HOW_THE_RATE_IS_CHOSEN, WHICH_RATE_APPLIES];

  // A general warning while no rate is applied (the crossed pair is servable either
  // way), but once one is, only if it describes that pair: FIX-published MXN/USD is a
  // certified rate, and keeping the sentence next to it would claim otherwise
  // (MR !1203 S5, scenario 2 of LEN-1809).
  if (!applied || applied.indicative) {
    parts.push(CROSSED_PAIR_IS_INDICATIVE);
  }

  // A native reading converts nothing, so there is no factor to defend. The backend
  // omits `fx` entirely in that case and the sentence would be `1 MXN = 1.000000 MXN`.
  if (applied && applied.from !== applied.to) {
    parts.push(`${appliedFactor(applied)}${appliedDate(applied)}.`);
  }

  return parts.join(' ');
}

/** The text with no rate applied, for templates that cannot call a function. */
export const FX_DISCLOSURE = fxDisclosure();

/**
 * The disclosure as one printed line for an export header (LEN-2092).
 *
 * A PDF taken to committee is the audit scenario LEN-1809 names: on screen the
 * reader can reopen the tooltip, in a document whatever is not printed is
 * lost. Built from the same pieces as the tooltip so the two cannot disagree —
 * the first version of this line (LEN-2136, `overview-fx-copy.ts`) lived apart
 * and had already drifted on the date format and the factor's wording.
 *
 * Without a conversion it still names the currency ("Amounts presented in
 * MXN", scenario 6 of LEN-1810): that is identification, not a rate claim, so
 * criterion 4 of LEN-2092 (no strip for a native view) still holds.
 *
 * The closing factor printed is the one the tooltip showed; the trailing
 * flows sentence is what distinguishes the two bases when the exported view
 * mixes balance and flow charts (scope 3).
 *
 * @param opts.mixedBases - whether the exported view holds both balances and
 *   flows. Defaults to true (Overview and Facility Insights do); pass false
 *   for a balances-only view like Concentrations, where the flows sentence
 *   would describe charts that are not in the document (MR !1207 S5).
 */
export function fxExportLine(
  currency: string | undefined,
  applied?: FxDisclosureWire,
  opts: { mixedBases?: boolean } = {}
): string {
  if (!currency) {
    return '';
  }

  const presented = `Amounts presented in ${currency}`;

  // The factor must describe the currency being printed: a native reading has
  // nothing to defend, and a factor targeting another currency (a leftover
  // from a different selection) would caption COP amounts with an MXN line.
  // The state's invalidation already prevents that upstream, but the single
  // source of truth should hold its own invariant (MR !1207 S1).
  if (!applied || applied.from === applied.to || applied.to !== currency) {
    return presented;
  }

  // Same reasoning as the tooltip (S5 of MR !1203): the marker belongs to the
  // derived pair only — printing it next to a FIX-certified factor would
  // claim the authority certifies less than it does.
  const indicative = applied.indicative
    ? '; indicative — crossed via USD, not binding'
    : '';

  // Only where it adds information: next to a closing factor in a view that
  // mixes both kinds. Next to an average factor it would be circular
  // ("Applied average rate… flows convert at the average rate"), and in a
  // balances-only view it names a basis no figure used (MR !1207 S3/S5).
  const flowsRule =
    opts.mixedBases === false || applied.basis !== 'closing'
      ? ''
      : ' Flows convert at the average rate of their period (IAS 21).';

  return (
    `${presented} — ${appliedFactor(applied)}${appliedDate(applied)} ` +
    `(${applied.source})${indicative}.${flowsRule}`
  );
}
